import {
  BookOutlined,
  CloseOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import { useMutation } from "@apollo/client/react";
import { Alert, App, Button, Flex, Space, Tabs, Typography } from "antd";
import { useState } from "react";
import { VersionTag } from "../../../components/VersionTag.tsx";
import { Link, useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { BreadcrumbTrail } from "../../../components/BreadcrumbTrail.tsx";
import { DeleteRecipeDocument, RestoreRecipeDocument } from "../../../gql/generated/graphqlSchema.tsx";
import { useTrailLabel } from "../../../hooks/useTrailLabel.ts";
import { routes } from "../../../routes.ts";
import { useRecipe } from "../RecipeContext.tsx";
import { DuplicateRecipeModal } from "../DuplicateRecipeModal.tsx";
import { RecipeUsedAsSubRecipeTab } from "../RecipeUsedAsSubRecipeTab.tsx";
import { RecipeGeneralTab } from "./RecipeGeneralTab.tsx";
import { RecipeIngredientsTab } from "./RecipeIngredientsTab.tsx";
import { RecipeNutritionTab } from "./RecipeNutritionTab.tsx";
import { RecipeLabelTab } from "../edit/RecipeLabelTab.tsx";
import { RecipePhotosTab } from "./RecipePhotosTab.tsx";
import { RecipeTab } from "../constants.ts";
import { banner, title } from "../../../styles/shared.styles.ts";

const viewTabSchema = z.enum(RecipeTab).catch(RecipeTab.General);

export const RecipeDetailView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { modal, message } = App.useApp();
  const { recipe, readOnly, refetch } = useRecipe();
  useTrailLabel(readOnly ? `${recipe.name} v${recipe.versionNumber}` : recipe.name, BookOutlined);

  const [deleteRecipe, { loading: deleting }] = useMutation(DeleteRecipeDocument);
  const [restoreRecipe, { loading: restoring }] = useMutation(RestoreRecipeDocument);
  const [duplicateOpen, setDuplicateOpen] = useState(false);

  const activeTab = viewTabSchema.parse(location.hash.replace("#", ""));

  const handleDelete = () => {
    modal.confirm({
      title: t("recipes.deleteTitle"),
      content: t("recipes.deleteConfirm"),
      okButtonProps: { danger: true, icon: <DeleteOutlined /> },
      cancelButtonProps: { icon: <CloseOutlined /> },
      okText: t("recipes.delete"),
      onOk: async () => {
        await deleteRecipe({ variables: { id: recipe.id } });
        void message.success(t("recipes.deletedDone"));
        void refetch();
      },
    });
  };

  const handleRestore = () => {
    modal.confirm({
      title: t("recipes.restoreTitle"),
      content: t("recipes.restoreConfirm"),
      okButtonProps: { icon: <UndoOutlined /> },
      cancelButtonProps: { icon: <CloseOutlined /> },
      okText: t("recipes.restore"),
      onOk: async () => {
        await restoreRecipe({ variables: { id: recipe.id } });
        void message.success(t("recipes.restoredDone"));
        void refetch();
      },
    });
  };

  return (
    <Flex gap="middle" vertical>
      <Flex gap="small" vertical>
        <BreadcrumbTrail />
        <Space wrap>
          <Typography.Title className={title} level={3}>
            {recipe.name}
          </Typography.Title>
          <VersionTag entity={recipe} />
        </Space>
        {recipe.deleted ? (
          <Alert
            action={
              <Button icon={<UndoOutlined />} loading={restoring} onClick={handleRestore}>
                {t("recipes.restore")}
              </Button>
            }
            className={banner}
            showIcon
            title={t("recipes.deletedBanner")}
            type="error"
          />
        ) : readOnly ? (
          <Alert
            action={<Link to={routes.recipeDetail.to(recipe.id)}>{t("recipes.archivedVersionBannerAction")}</Link>}
            className={banner}
            showIcon
            title={t("recipes.archivedVersionBannerText")}
            type="warning"
          />
        ) : recipe.hasDraft ? (
          <Alert
            action={
              <Button
                icon={<EditOutlined />}
                onClick={() => void navigate({ pathname: routes.recipeDetailEdit.to(recipe.id), hash: location.hash })}
                type="primary"
              >
                {t("recipes.continueEditing")}
              </Button>
            }
            className={banner}
            showIcon
            title={t("recipes.draftBannerText")}
            type="info"
          />
        ) : (
          <Space wrap>
            <Button
              icon={<EditOutlined />}
              onClick={() => void navigate({ pathname: routes.recipeDetailEdit.to(recipe.id), hash: location.hash })}
              type="primary"
            >
              {t("recipes.edit")}
            </Button>
            <Button icon={<CopyOutlined />} onClick={() => setDuplicateOpen(true)}>
              {t("recipes.duplicate")}
            </Button>
            <Button danger icon={<DeleteOutlined />} loading={deleting} onClick={handleDelete}>
              {t("recipes.delete")}
            </Button>
          </Space>
        )}
      </Flex>

      <DuplicateRecipeModal
        onClose={() => setDuplicateOpen(false)}
        open={duplicateOpen}
        recipeId={recipe.id}
        recipeName={recipe.name}
      />

      <Tabs
        activeKey={activeTab}
        destroyOnHidden
        items={[
          { key: RecipeTab.General, label: t("recipes.tabGeneral"), children: <RecipeGeneralTab /> },
          {
            key: RecipeTab.Ingredients,
            label: t("recipes.tabIngredients", { count: recipe.ingredients.length }),
            children: <RecipeIngredientsTab />,
          },
          { key: RecipeTab.Nutrition, label: t("recipes.tabNutrition"), children: <RecipeNutritionTab /> },
          { key: RecipeTab.Label, label: t("recipes.tabLabel"), children: <RecipeLabelTab /> },
          {
            key: RecipeTab.Photos,
            label: t("recipes.tabPhotos", { count: recipe.photos.length }),
            children: <RecipePhotosTab />,
          },
          {
            key: RecipeTab.UsedAsSubRecipe,
            label: t("recipes.tabUsedAsSubRecipe", { count: recipe.usedAsSubRecipeIn.length }),
            children: <RecipeUsedAsSubRecipeTab />,
          },
        ]}
        onChange={(tab) => void navigate({ hash: tab }, { replace: true })}
      />
    </Flex>
  );
};
